/**
 * Envíos de documentos entre sucursales — cabecera para las transferencias.
 *
 * Sustituye la Creación (un chasis a la vez) y la Recepción (renglones
 * sueltos) del hub de Inventario de Documentos:
 *   - CrearEnvio: N chasis con sus tipos de documento en UN envío, con guía
 *     imprimible para el transportista.
 *   - Envios: entrantes (para recibir con checklist: lo que no llegó queda
 *     "en reclamo" en vez de desaparecer), salientes (seguimiento) y todos;
 *     rechazo con razón, nunca borrado.
 *
 * Las transferencias anteriores a la cabecera (envio_id NULL) se siguen
 * recibiendo con la pantalla anterior, que el listado enlaza mientras queden
 * en vuelo.
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { DataGrid } from "@mui/x-data-grid";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { useGlobalMDAlert } from "context";
import { FP, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";
import { labelTipo } from "lib/documentosvehiculo";
import { encabezadoEmpresaHtml, imprimirHtml } from "lib/impresionhtml";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};
// El tema (assets/theme/components/table/tableHead) pone display:block al
// TableHead y desalinea las columnas.
const tableHeadSx = { display: "table-header-group", p: 0, borderRadius: 0 };
const celda = { py: 0.6, px: 1, fontSize: "0.82rem", borderColor: FP.color.border };

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}
SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

const ESTADO_ENVIO = {
  "en transito": { label: "En tránsito", bg: "#FFF3E0", fg: "#E65100" },
  recibido: { label: "Recibido", bg: "#E8F5E9", fg: "#2E7D32" },
  "recibido parcial": { label: "Recibido parcial", bg: "#FFEBEE", fg: "#C62828" },
  rechazado: { label: "Rechazado", bg: "#ECEFF1", fg: "#455A64" },
};
const ESTADO_LINEA = {
  "En progreso": { label: "En tránsito", bg: "#FFF3E0", fg: "#E65100" },
  Completado: { label: "Recibido", bg: "#E8F5E9", fg: "#2E7D32" },
  "En reclamo": { label: "En reclamo", bg: "#FFEBEE", fg: "#C62828" },
  Rechazado: { label: "Rechazado", bg: "#ECEFF1", fg: "#455A64" },
};
const chip = (estado, mapa) => {
  const c = mapa[estado] || { label: estado, bg: "#ECEFF1", fg: "#455A64" };
  return <Chip size="small" label={c.label} sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700 }} />;
};

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** Guía de transferencia: relación firmable para el transportista. */
function imprimirGuia({ envio, lineas, compania }) {
  const filas = lineas
    .map(
      (l, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-family:monospace;font-weight:700">${esc(l.chasis)}</td>
        <td>${esc([l.marca, l.modelo, l.colore].filter(Boolean).join(" · "))}</td>
        <td>${esc(l.placa)}</td>
        <td>${esc(labelTipo(l.documento))}</td>
        <td>${esc((ESTADO_LINEA[l.status] || { label: l.status }).label)}</td>
      </tr>`
    )
    .join("");
  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light">
    <title>Guía de transferencia #${envio.id}</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#111;margin:24px}
      h2{text-align:center;margin:14px 0 4px}
      .meta{display:flex;flex-wrap:wrap;gap:6px 24px;margin:10px 0 14px;padding:10px;border:1px solid #999;border-radius:6px}
      .meta b{display:inline-block;min-width:90px}
      table{width:100%;border-collapse:collapse;margin-top:6px}
      th,td{border:1px solid #999;padding:5px 6px;text-align:left;vertical-align:top}
      th{background:#eee}
      .firmas{display:flex;justify-content:space-between;gap:40px;margin-top:56px}
      .firmas div{flex:1;text-align:center;border-top:1px solid #111;padding-top:6px}
      @media print{body{margin:12mm}}
    </style></head>
    <body>
      ${encabezadoEmpresaHtml(compania)}
      <h2>GUÍA DE TRANSFERENCIA DE DOCUMENTOS #${envio.id}</h2>
      <div class="meta">
        <span><b>Origen:</b> ${esc(envio.origen)}</span>
        <span><b>Destino:</b> ${esc(envio.destino)}</span>
        <span><b>Fecha:</b> ${esc(envio.fecha_crea)}</span>
        <span><b>Emitido por:</b> ${esc(envio.user)}</span>
        <span><b>Transportista:</b> ${esc(envio.transportista || "—")}</span>
        <span><b>Estado:</b> ${esc(
          (ESTADO_ENVIO[envio.estado] || { label: envio.estado }).label
        )}</span>
        ${
          envio.comentario
            ? `<span style="flex-basis:100%"><b>Comentario:</b> ${esc(envio.comentario)}</span>`
            : ""
        }
      </div>
      <table>
        <tr><th>#</th><th>Chasis</th><th>Vehículo</th><th>Placa</th><th>Documento</th><th>Estado</th></tr>
        ${filas}
      </table>
      <p style="margin-top:8px"><b>Total:</b> ${lineas.length} documento(s) de ${
    new Set(lineas.map((l) => l.chasis)).size
  } chasis.</p>
      <div class="firmas">
        <div>Entrega (${esc(envio.origen)})</div>
        <div>Transportista</div>
        <div>Recibe (${esc(envio.destino)})</div>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

// ---------------------------------------------------------------------------
// Crear envío
// ---------------------------------------------------------------------------

export function CrearEnvio({ onCreado }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  // `todas`: el destino necesita las demás sucursales aunque el usuario no
  // sea administrador.
  const sucursales = useSucursales({ todas: true });

  const [candidatos, setCandidatos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [destino, setDestino] = useState(null);
  const [transportista, setTransportista] = useState("");
  const [comentario, setComentario] = useState("");
  // [{chasis, marca…, disponibles, tipos: Set seleccionados}]
  const [seleccion, setSeleccion] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/envio_candidatos", {});
      if (ok) setCandidatos(data.candidatos || []);
      else showAlert({ message: data.Error || "No se pudieron cargar los chasis", color: "error" });
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const agregar = (c) => {
    if (!c || seleccion.some((s) => s.chasis === c.chasis)) return;
    setSeleccion((prev) => [...prev, { ...c, tipos: [...c.disponibles] }]);
  };
  const quitar = (chasis) => setSeleccion((prev) => prev.filter((s) => s.chasis !== chasis));
  const toggleTipo = (chasis, tipo) =>
    setSeleccion((prev) =>
      prev.map((s) =>
        s.chasis !== chasis
          ? s
          : {
              ...s,
              tipos: s.tipos.includes(tipo)
                ? s.tipos.filter((t) => t !== tipo)
                : [...s.tipos, tipo],
            }
      )
    );
  const totalDocs = seleccion.reduce((a, s) => a + s.tipos.length, 0);
  const disponibles = candidatos.filter((c) => !seleccion.some((s) => s.chasis === c.chasis));

  const crear = async () => {
    if (!destino) {
      showAlert({ message: "Seleccione la sucursal de destino", color: "warning" });
      return;
    }
    const items = seleccion
      .filter((s) => s.tipos.length)
      .map((s) => ({ chasis: s.chasis, tipos: s.tipos }));
    if (!items.length) {
      showAlert({ message: "Agregue al menos un chasis con documentos", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/envio_crear", {
        destino: destino.label,
        transportista,
        comentario,
        items,
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo crear el envío", color: "error" });
        return;
      }
      showAlert({ message: data.mensajeok, color: "success" });
      const guia = await post("/api/envio_guia", { id: data.id });
      if (guia.ok) imprimirGuia(guia.data);
      setSeleccion([]);
      setTransportista("");
      setComentario("");
      cargar();
      if (onCreado) onCreado(data.id);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box>
      <Box sx={sectionBox}>
        <SectionTitle icon={LocalShippingOutlinedIcon} color={FP.color.accentBlue}>
          Datos del envío
        </SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={sucursales}
              value={destino}
              onChange={(_, v) => setDestino(v)}
              getOptionLabel={(o) => o.label || ""}
              isOptionEqualToValue={(o, v) => o.label === v.label}
              renderInput={(p) => <TextField {...p} label="Sucursal de destino" sx={fpInputSx} />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Transportista"
              value={transportista}
              onChange={(e) => setTransportista(e.target.value)}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Comentario"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              sx={fpInputSx}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={sectionBox}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <SectionTitle icon={DescriptionOutlinedIcon} color={FP.color.success}>
            Documentos a enviar ({totalDocs} de {seleccion.length} chasis)
          </SectionTitle>
          <Button
            startIcon={
              guardando ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />
            }
            onClick={crear}
            disabled={guardando || totalDocs === 0}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Crear envío e imprimir guía
          </Button>
        </Box>
        <Autocomplete
          options={disponibles}
          loading={cargando}
          value={null}
          blurOnSelect
          onChange={(_, v) => agregar(v)}
          getOptionLabel={(o) => o.chasis || ""}
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.toLowerCase();
            return opts
              .filter((o) =>
                `${o.chasis} ${o.marca} ${o.modelo} ${o.placa}`.toLowerCase().includes(q)
              )
              .slice(0, 60);
          }}
          renderOption={(props, o) => (
            <li {...props} key={o.chasis}>
              <Box sx={{ width: "100%" }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", fontFamily: "monospace" }}>
                  {o.chasis}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                  {[o.marca, o.modelo, o.colore, o.placa && `Placa ${o.placa}`, o.sucmatricula]
                    .filter(Boolean)
                    .join(" · ")}{" "}
                  — {o.disponibles.map(labelTipo).join(", ")}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(p) => (
            <TextField
              {...p}
              label="Agregar chasis (buscar por chasis, marca, modelo o placa)"
              helperText={
                cargando
                  ? "Cargando…"
                  : `${disponibles.length} chasis con documentos en tu sucursal para enviar`
              }
              sx={{ ...fpInputSx, mb: 2 }}
            />
          )}
        />
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead sx={tableHeadSx}>
              <TableRow>
                <TableCell sx={{ ...celda, fontWeight: 800 }}>Chasis</TableCell>
                <TableCell sx={{ ...celda, fontWeight: 800 }}>Vehículo</TableCell>
                <TableCell sx={{ ...celda, fontWeight: 800 }}>
                  Documentos (clic para quitar/poner)
                </TableCell>
                <TableCell sx={{ ...celda, width: 60 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {seleccion.map((s) => (
                <TableRow key={s.chasis}>
                  <TableCell sx={{ ...celda, fontFamily: "monospace", fontWeight: 700 }}>
                    {s.chasis}
                  </TableCell>
                  <TableCell sx={celda}>
                    {[s.marca, s.modelo, s.colore, s.placa && `Placa ${s.placa}`]
                      .filter(Boolean)
                      .join(" · ")}
                  </TableCell>
                  <TableCell sx={celda}>
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                      {s.disponibles.map((t) => {
                        const on = s.tipos.includes(t);
                        return (
                          <Chip
                            key={t}
                            size="small"
                            label={labelTipo(t)}
                            icon={on ? <TaskAltOutlinedIcon /> : undefined}
                            onClick={() => toggleTipo(s.chasis, t)}
                            sx={{
                              fontWeight: 700,
                              bgcolor: on ? "#E8F5E9" : FP.color.surfaceAlt,
                              color: on ? "#2E7D32" : FP.color.textMuted,
                              textDecoration: on ? "none" : "line-through",
                              "& .MuiChip-icon": { color: "#2E7D32" },
                            }}
                          />
                        );
                      })}
                      {/* Tipos que no se ofrecen, con el porqué: en tránsito,
                          entregados o en otra sucursal. */}
                      {Object.entries(s.ubicaciones || {}).filter(
                        ([t]) => !s.disponibles.includes(t)
                      ).length > 0 && (
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            color: FP.color.textMuted,
                            alignSelf: "center",
                          }}
                        >
                          (
                          {Object.entries(s.ubicaciones || {})
                            .filter(([t]) => !s.disponibles.includes(t))
                            .map(([t, donde]) => `${labelTipo(t)}: ${donde}`)
                            .join(" · ")}
                          )
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={celda}>
                    <Button
                      size="small"
                      onClick={() => quitar(s.chasis)}
                      sx={{ ...btnBase, color: "#C62828", minWidth: 0 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {seleccion.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    sx={{ ...celda, textAlign: "center", color: FP.color.textMuted, py: 3 }}
                  >
                    Busca arriba y agrega los chasis; cada uno entra con todos sus documentos
                    disponibles marcados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}
CrearEnvio.propTypes = { onCreado: PropTypes.func };
CrearEnvio.defaultProps = { onCreado: null };

// ---------------------------------------------------------------------------
// Envíos: entrantes (recibir), salientes (seguimiento), todos
// ---------------------------------------------------------------------------

function DetalleEnvio({ id, misucursal, onExit, onChanged }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [envio, setEnvio] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [marcadas, setMarcadas] = useState(new Set());
  const [comentario, setComentario] = useState("");
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [rechazoOpen, setRechazoOpen] = useState(false);
  const [razon, setRazon] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/envio_datos", { id });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo cargar el envío", color: "error" });
        return;
      }
      setEnvio(data.envio);
      setLineas(data.lineas || []);
      // Por defecto marcadas todas las que se pueden recibir.
      setMarcadas(
        new Set(
          (data.lineas || [])
            .filter((l) => ["En progreso", "En reclamo"].includes(l.status))
            .map((l) => l.id)
        )
      );
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, post]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (cargando && !envio) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!envio) return null;

  const soyDestino = String(envio.destino).split("-")[0].trim() === String(misucursal);
  const abierto = ["en transito", "recibido parcial"].includes(envio.estado);
  const recibibles = lineas.filter((l) => ["En progreso", "En reclamo"].includes(l.status));
  const puedeRecibir = soyDestino && abierto && recibibles.length > 0;
  const todas = recibibles.length > 0 && recibibles.every((l) => marcadas.has(l.id));

  const ejecutar = async (endpoint, body, exito) => {
    setGuardando(true);
    try {
      const { ok, data } = await post(endpoint, body);
      if (!ok) {
        showAlert({ message: data.Error || "Error", color: "error" });
        return false;
      }
      showAlert({ message: data.mensajeok || exito, color: "success" });
      cargar();
      if (onChanged) onChanged();
      return true;
    } finally {
      setGuardando(false);
    }
  };

  const imprimir = async () => {
    const { ok, data } = await post("/api/envio_guia", { id });
    if (ok) imprimirGuia(data);
    else showAlert({ message: data.Error || "No se pudo obtener la guía", color: "error" });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: FP.color.surface, minHeight: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>Envío #{envio.id}</Typography>
          <Typography sx={{ fontSize: "0.8rem", color: FP.color.textMuted }}>
            {envio.origen} → {envio.destino} · {envio.fecha_crea} · {envio.user}
            {envio.transportista && ` · Transportista: ${envio.transportista}`}
          </Typography>
          {envio.comentario && (
            <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary }}>
              {envio.comentario}
            </Typography>
          )}
          {envio.estado !== "en transito" && (
            <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary, mt: 0.4 }}>
              {envio.estado === "rechazado"
                ? `Rechazado por ${envio.recepuser}: ${envio.razonrechazo}`
                : `Recibido por ${envio.recepuser} el ${envio.fecharecepcion}${
                    envio.comentariorecepcion ? ` — ${envio.comentariorecepcion}` : ""
                  }`}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {chip(envio.estado, ESTADO_ENVIO)}
          <Button
            startIcon={<PrintOutlinedIcon />}
            onClick={imprimir}
            sx={btnOutline(FP.color.navy)}
          >
            Guía
          </Button>
          {soyDestino && envio.estado === "en transito" && (
            <Button
              startIcon={<BlockOutlinedIcon />}
              onClick={() => {
                setRazon("");
                setRechazoOpen(true);
              }}
              sx={btnOutline("#C62828")}
            >
              Rechazar
            </Button>
          )}
          {onExit && (
            <Button onClick={onExit} sx={btnOutline(FP.color.navy)}>
              Volver
            </Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ ...sectionBox, mb: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <SectionTitle icon={DescriptionOutlinedIcon} color={FP.color.success}>
            Documentos ({lineas.length})
          </SectionTitle>
          {puedeRecibir && (
            <Button
              startIcon={
                guardando ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Inventory2OutlinedIcon />
                )
              }
              onClick={() =>
                ejecutar(
                  "/api/envio_recibir",
                  { id, recibidas: [...marcadas], comentario },
                  "Envío recibido"
                )
              }
              disabled={guardando || marcadas.size === 0}
              sx={btnSolid(FP.color.success, "#2E7D32")}
            >
              Recibir {marcadas.size} de {recibibles.length}
            </Button>
          )}
        </Box>
        {puedeRecibir && (
          <TextField
            fullWidth
            size="small"
            label="Comentario de recepción (opcional)"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            sx={{ ...fpInputSx, mb: 1.5 }}
          />
        )}
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead sx={tableHeadSx}>
              <TableRow>
                {puedeRecibir && (
                  <TableCell padding="checkbox" sx={celda}>
                    <Checkbox
                      size="small"
                      checked={todas}
                      indeterminate={marcadas.size > 0 && !todas}
                      onChange={(e) =>
                        setMarcadas(
                          e.target.checked ? new Set(recibibles.map((l) => l.id)) : new Set()
                        )
                      }
                    />
                  </TableCell>
                )}
                <TableCell sx={{ ...celda, fontWeight: 800 }}>Chasis</TableCell>
                <TableCell sx={{ ...celda, fontWeight: 800 }}>Vehículo</TableCell>
                <TableCell sx={{ ...celda, fontWeight: 800 }}>Documento</TableCell>
                <TableCell sx={{ ...celda, fontWeight: 800 }}>Estado</TableCell>
                <TableCell sx={{ ...celda, fontWeight: 800 }}>Recibido</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineas.map((l) => {
                const recibible = ["En progreso", "En reclamo"].includes(l.status);
                return (
                  <TableRow key={l.id}>
                    {puedeRecibir && (
                      <TableCell padding="checkbox" sx={celda}>
                        <Checkbox
                          size="small"
                          disabled={!recibible}
                          checked={recibible && marcadas.has(l.id)}
                          onChange={(e) =>
                            setMarcadas((prev) => {
                              const n = new Set(prev);
                              if (e.target.checked) n.add(l.id);
                              else n.delete(l.id);
                              return n;
                            })
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ ...celda, fontFamily: "monospace", fontWeight: 700 }}>
                      {l.chasis}
                    </TableCell>
                    <TableCell sx={celda}>
                      {[l.marca, l.modelo, l.colore, l.placa && `Placa ${l.placa}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </TableCell>
                    <TableCell sx={celda}>{labelTipo(l.documento)}</TableCell>
                    <TableCell sx={celda}>{chip(l.status, ESTADO_LINEA)}</TableCell>
                    <TableCell sx={celda}>
                      {l.fecharecep &&
                        `${l.fecharecep}${l.receptoruser ? ` · ${l.receptoruser}` : ""}`}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        {puedeRecibir && (
          <Typography sx={{ mt: 1.2, fontSize: "0.75rem", color: FP.color.textMuted }}>
            Lo que no marques queda <b>en reclamo</b>: el envío se cierra como recibido parcial y
            esos documentos se pueden recibir después si aparecen.
          </Typography>
        )}
      </Box>

      <Dialog open={rechazoOpen} onClose={() => setRechazoOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Rechazar envío #{envio.id}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            sx={{ mt: 1, ...fpInputSx }}
            label="Razón del rechazo"
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRechazoOpen(false)} sx={btnOutline(FP.color.navy)}>
            Cerrar
          </Button>
          <Button
            onClick={async () => {
              const ok = await ejecutar("/api/envio_rechazar", { id, razon }, "Envío rechazado");
              if (ok) setRechazoOpen(false);
            }}
            disabled={guardando || !razon.trim()}
            sx={btnSolid("#C62828", "#B71C1C")}
          >
            Rechazar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
DetalleEnvio.propTypes = {
  id: PropTypes.number.isRequired,
  misucursal: PropTypes.string.isRequired,
  onExit: PropTypes.func,
  onChanged: PropTypes.func,
};
DetalleEnvio.defaultProps = { onExit: null, onChanged: null };

export function Envios({ direccionInicial, renderLegado }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [direccion, setDireccion] = useState(direccionInicial);
  const [rows, setRows] = useState([]);
  const [misucursal, setMisucursal] = useState("");
  const [legadas, setLegadas] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [detalleId, setDetalleId] = useState(null);
  const [legadoOpen, setLegadoOpen] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/envio_listar", { direccion });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudieron cargar los envíos", color: "error" });
        return;
      }
      setRows(data.envios || []);
      setMisucursal(data.misucursal || "");
      setLegadas(data.legadas_en_transito || 0);
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, direccion]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const columnas = [
    { field: "id", headerName: "#", width: 70 },
    { field: "fecha_crea", headerName: "Fecha", width: 130 },
    { field: "origen", headerName: "Origen", flex: 1, minWidth: 120 },
    { field: "destino", headerName: "Destino", flex: 1, minWidth: 120 },
    { field: "transportista", headerName: "Transportista", flex: 0.9, minWidth: 110 },
    {
      field: "estado",
      headerName: "Estado",
      width: 140,
      renderCell: (p) => chip(p.value, ESTADO_ENVIO),
    },
    {
      field: "total",
      headerName: "Documentos",
      width: 150,
      renderCell: (p) => (
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
          {p.row.recibidos}/{p.row.total} · {p.row.chasis} chasis
          {p.row.reclamo > 0 && (
            <Typography component="span" sx={{ fontSize: "0.75rem", color: "#C62828", ml: 0.6 }}>
              ({p.row.reclamo} reclamo)
            </Typography>
          )}
        </Typography>
      ),
    },
    {
      field: "abrir",
      headerName: "",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Button
          size="small"
          onClick={() => setDetalleId(p.row.id)}
          sx={{ ...btnBase, color: FP.color.navy }}
        >
          {p.row.estado === "en transito" && direccion === "entrantes" ? "Recibir" : "Abrir"}
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={direccion}
          onChange={(_, v) => v && setDireccion(v)}
          sx={{
            bgcolor: FP.color.cardBg,
            "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 700, px: 2 },
          }}
        >
          <ToggleButton value="entrantes">Para recibir (hacia mi sucursal)</ToggleButton>
          <ToggleButton value="salientes">Enviados (desde mi sucursal)</ToggleButton>
          <ToggleButton value="todos">Todos</ToggleButton>
        </ToggleButtonGroup>
        <Button
          startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          onClick={cargar}
          disabled={cargando}
          sx={btnOutline(FP.color.navy)}
        >
          Actualizar
        </Button>
      </Box>

      {legadas > 0 && renderLegado && (
        <Box
          sx={{
            ...sectionBox,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            borderColor: "#FFB74D",
            bgcolor: "#FFF8E1",
          }}
        >
          <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: "#E65100" }}>
            Hay {legadas} documento(s) en tránsito hacia tu sucursal de transferencias anteriores
            (sin envío).
          </Typography>
          <Button onClick={() => setLegadoOpen(true)} sx={btnOutline("#E65100")}>
            Recibirlos con la pantalla anterior
          </Button>
        </Box>
      )}

      <DataGrid
        autoHeight
        rows={rows}
        columns={columnas}
        loading={cargando}
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        sx={fpDataGridSx}
      />

      <Dialog fullScreen open={detalleId != null} onClose={() => setDetalleId(null)}>
        {detalleId != null && (
          <DetalleEnvio
            id={detalleId}
            misucursal={misucursal}
            onExit={() => setDetalleId(null)}
            onChanged={cargar}
          />
        )}
      </Dialog>

      {renderLegado && (
        <Dialog fullScreen open={legadoOpen} onClose={() => setLegadoOpen(false)}>
          <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setLegadoOpen(false)} sx={btnOutline(FP.color.navy)}>
              Volver
            </Button>
          </Box>
          {renderLegado()}
        </Dialog>
      )}
    </Box>
  );
}
Envios.propTypes = {
  direccionInicial: PropTypes.oneOf(["entrantes", "salientes", "todos"]),
  renderLegado: PropTypes.func,
};
Envios.defaultProps = { direccionInicial: "entrantes", renderLegado: null };
